import React, { forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomModal, BuySellDetailsMRT } from '@components';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography
} from '@mui/material';
import { apiBuyContract, apiGetOrder } from '@store';
import { showToast, getMargin } from '@utils';

export const Buy = forwardRef(
    ({ handleClose, selectedContract, user, sym, open, chf }, ref) => {
        const dispatch = useDispatch();
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);
        const [submitting, setSubmitting] = useState(false);

        const [state, setState] = useState({
            market: 'THE',
            type: 'buy',
            price_selected: '',
            seller: '',
            buyer: user?.CompanyShort,
            deliverypoint: 'THE',
            volume: '',
            exitpoint: '',
            price_percent: '100',
            ordertype: 'internal',
            currency: 'CHF',
            sym: selectedContract?.contract || sym
        });

        const [margin, setMargin] = useState(0);

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state.volume]);

        useEffect(() => {
            if (user?.CanBuyFrom?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    seller: user.CanBuyFrom[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState({
                ...state,
                [name]: value
            });
        };

        const [type, setType] = useState('ask');

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            const volume = parseFloat(state.volume);
            const ask = parseFloat(selectedContract?.ask);

            const data = {
                ...state,
                margin: margin,
                total_price: margin + ask,
                volume,
                price: { ask: ask },
                exitpoint: state.exitpoint || {}
            };

            const resp = await dispatch(apiBuyContract(data));
            if (resp?.meta?.requestStatus === 'fulfilled') {
                handleClose();
                dispatch(apiGetOrder());
                showToast('success', 'Purchase Order Saved');
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast('error', resp?.payload || 'Error buying contract');
            }
            setSubmitting(false);
        };

        // timer
        const [timer, setTimer] = useState(25);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (ref?.current) {
                setTimer(25);
            }
        }, [ref]);

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleSubmit}
                modalTitle={`Buy Contract`}
                buttonText={'Buy'}
                timer={timer}
                disabled={submitting || (state.volume ? false : true)}
                ref={ref}
            >
                <Box className="flex flex-col gap-3">
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                            shrink
                        >
                            Please Select what you would like to buy
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <MenuItem value={'ask'}>Ask Price</MenuItem>
                        </Select>
                    </FormControl>
                    <Box className="flex gap-3">
                        <Typography>Contract:</Typography>
                        <Typography color={'black'}>
                            {selectedContract?.contract || sym}
                        </Typography>
                    </Box>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel
                            id="contractLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Volume
                        </InputLabel>
                        <OutlinedInput
                            id="volume"
                            label="Volume"
                            name="volume"
                            required
                            type="number"
                            onChange={handleChange}
                            value={state.volume}
                            inputProps={{ step: 0.01, min: 0 }}
                            endAdornment={
                                <InputAdornment position="start" sx={{ ml: 2 }}>
                                    MW
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Endpoint
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Endpoint"
                            name="exitpoint"
                            value={state.exitpoint}
                            onChange={handleChange}
                        >
                            {user?.Endpoints?.map((endpoint, index) => (
                                <MenuItem key={index} value={endpoint}>
                                    {endpoint.name} - {endpoint.location}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel
                            id="demo-simple-select-label"
                            sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                        >
                            Seller
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Seller"
                            name="seller"
                            value={state.seller}
                            onChange={handleChange}
                        >
                            {user?.CanBuyFrom?.map((seller, index) => (
                                <MenuItem key={index} value={seller}>
                                    {seller}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth variant="outlined" disabled>
                        <InputLabel
                            id="deliverypointLabel"
                            sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                        >
                            Delivery Point
                        </InputLabel>
                        <OutlinedInput
                            id="deliverypoint"
                            label="Delivery Point"
                            name="deliverypoint"
                            value={state.deliverypoint || 'THE'}
                        />
                    </FormControl>
                   <BuySellDetailsMRT
                        price={selectedContract?.bid}
                        margin={margin || 0}
                        totalPrice={(parseFloat(selectedContract?.bid) - margin).toFixed(3)}
                        currency="CHF"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={state?.volume ? (selectedContract?.contract_hours * (parseFloat(selectedContract?.bid) - margin) * parseFloat(state.volume)).toFixed(3) : 0}
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                </Box>
            </CustomModal>
        );
    }
);
